import { z } from 'zod';
import { PLAN_FEATURE_KEYS } from '../plan-features.js';
import {
  NOTIFICATION_SEVERITIES,
  ORGANIZATION_STATUSES,
  SUBSCRIPTION_STATUSES,
} from '../constants.js';
import { nameSchema } from './common.js';

/* ------------------------------------------------------------------- dashboard */

export const dashboardSummarySchema = z.object({
  screens: z.object({
    total: z.number().int(),
    online: z.number().int(),
    offline: z.number().int(),
    syncing: z.number().int(),
    warning: z.number().int(),
    needsAttention: z.number().int(),
    unpaired: z.number().int(),
  }),
  locations: z.number().int(),
  media: z.object({ count: z.number().int(), storageBytes: z.number().int() }),
  storage: z.object({ usedBytes: z.number().int(), limitBytes: z.number().int() }),
  playlists: z.object({ total: z.number().int(), active: z.number().int() }),
  schedules: z.object({ total: z.number().int(), activeNow: z.number().int() }),
  activeCampaigns: z.number().int(),
  recentlyConnected: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      locationName: z.string().nullable(),
      lastSeenAt: z.string().nullable(),
      status: z.string(),
    }),
  ),
  staleSync: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      locationName: z.string().nullable(),
      lastSyncAt: z.string().nullable(),
      status: z.string(),
    }),
  ),
  uptimeSeries: z.array(z.object({ date: z.string(), online: z.number(), offline: z.number() })),
  playsSeries: z.array(z.object({ date: z.string(), plays: z.number() })),
});

/* --------------------------------------------------------------- notifications */

export const notificationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  type: z.string(),
  severity: z.enum(NOTIFICATION_SEVERITIES),
  title: z.string(),
  body: z.string().nullable(),
  screenId: z.string().nullable(),
  screenName: z.string().nullable(),
  readAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const notificationSettingsSchema = z.object({
  offlineMinutes: z.number().int().min(1).max(1440).default(15),
  staleSyncHours: z.number().int().min(1).max(168).default(24),
  lowStoragePercent: z.number().int().min(1).max(90).default(10),
  emailEnabled: z.boolean().default(true),
  emailRecipients: z.array(z.string().email()).max(20).default([]),
  webhookUrl: z.string().url().nullable().optional(),
  webhookSecret: z.string().max(200).nullable().optional(),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

/* -------------------------------------------------------------------- analytics */

export const analyticsQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  screenId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  playlistId: z.string().uuid().optional(),
  mediaId: z.string().uuid().optional(),
});

export const analyticsOverviewSchema = z.object({
  totalPlays: z.number().int(),
  totalDurationMs: z.number().int(),
  uniqueMedia: z.number().int(),
  activeScreens: z.number().int(),
  byDate: z.array(z.object({ date: z.string(), plays: z.number(), durationMs: z.number() })),
  topMedia: z.array(
    z.object({
      mediaId: z.string(),
      name: z.string(),
      plays: z.number(),
      durationMs: z.number(),
    }),
  ),
  topPlaylists: z.array(
    z.object({ playlistId: z.string(), name: z.string(), plays: z.number() }),
  ),
  byScreen: z.array(
    z.object({ screenId: z.string(), name: z.string(), plays: z.number(), durationMs: z.number() }),
  ),
  byLocation: z.array(z.object({ locationId: z.string(), name: z.string(), plays: z.number() })),
  uptime: z.array(
    z.object({
      screenId: z.string(),
      name: z.string(),
      onlineSeconds: z.number(),
      offlineSeconds: z.number(),
      uptimePercent: z.number(),
    }),
  ),
});

/* --------------------------------------------------------------------- billing */

export const planSchema = z.object({
  key: z.string(),
  name: z.string(),
  nameAr: z.string().nullable(),
  description: z.string().nullable(),
  descriptionAr: z.string().nullable(),
  maxScreens: z.number().int(),
  maxStorageBytes: z.number().int(),
  maxUsers: z.number().int(),
  priceCents: z.number().int(),
  currency: z.string(),
  interval: z.string(),
  features: z.array(z.string()),
  featuresAr: z.array(z.string()),
  contactSales: z.boolean(),
  trialDays: z.number().int(),
  isPublic: z.boolean(),
  sortOrder: z.number().int(),
  /** The yearly price, when the plan can also be paid yearly. */
  priceYearlyCents: z.number().int().nullable(),
  /** Features the plan includes (see PLAN_FEATURES); the API enforces them. */
  capabilities: z.array(z.enum(PLAN_FEATURE_KEYS)),
  /** The list the website and billing page show, built from the limits and features above. */
  featureList: z.array(
    z.object({ key: z.string(), label: z.string(), labelAr: z.string(), included: z.boolean() }),
  ),
  /** Which billing periods can be bought online by card (they have a payment-provider price). */
  payOnline: z.object({ month: z.boolean(), year: z.boolean() }),
  /** Paddle price ids; only in the platform admin's listing. */
  paddlePriceMonthly: z.string().nullable().optional(),
  paddlePriceYearly: z.string().nullable().optional(),
});
export type Plan = z.infer<typeof planSchema>;

const planText = (max: number) => z.string().trim().max(max);
const featureList = z.array(z.string().trim().min(1).max(120)).max(20);
const paddlePriceId = z
  .string()
  .trim()
  .regex(/^(pri_[a-z0-9]{10,40})?$/, 'A Paddle price id looks like pri_01h…')
  .nullable()
  .optional();

/** A plan as the platform admin edits it. The key is fixed once created. */
export const planInputSchema = z.object({
  name: planText(60).min(1),
  nameAr: planText(60).nullable().optional(),
  description: planText(300).nullable().optional(),
  descriptionAr: planText(300).nullable().optional(),
  maxScreens: z.number().int().min(0).max(1_000_000),
  maxStorageBytes: z.number().int().min(0).max(1024 ** 5),
  maxUsers: z.number().int().min(1).max(100_000),
  /** In the currency's smallest unit: cents, or fils for JOD. */
  priceCents: z.number().int().min(0).max(100_000_000),
  currency: z.enum(['usd', 'jod', 'sar', 'aed', 'eur']),
  interval: z.enum(['month', 'year']),
  features: featureList,
  featuresAr: featureList,
  contactSales: z.boolean(),
  trialDays: z.number().int().min(0).max(365),
  isPublic: z.boolean(),
  sortOrder: z.number().int().min(0).max(1000),
  priceYearlyCents: z.number().int().min(0).max(1_000_000_000).nullable().optional(),
  capabilities: z.array(z.enum(PLAN_FEATURE_KEYS)).max(PLAN_FEATURE_KEYS.length).optional(),
  /** Paddle price ids (pri_…), from Paddle → Catalog → the product's prices. */
  paddlePriceMonthly: paddlePriceId,
  paddlePriceYearly: paddlePriceId,
});
export type PlanInput = z.infer<typeof planInputSchema>;
export const createPlanSchema = planInputSchema.extend({
  key: z.string().trim().regex(/^[a-z][a-z0-9-]{1,30}$/, 'Use lowercase letters, numbers and dashes'),
});
export const updatePlanSchema = planInputSchema.partial();

export const subscriptionSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  planKey: z.string(),
  planName: z.string(),
  status: z.enum(SUBSCRIPTION_STATUSES),
  licensedScreens: z.number().int(),
  maxScreens: z.number().int(),
  maxStorageBytes: z.number().int(),
  currentPeriodEnd: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  provider: z.string(),
  externalCustomerId: z.string().nullable(),
  /** month | year, for plans paid through a payment provider. */
  billingInterval: z.string().nullable(),
  createdAt: z.string(),
});

export type Subscription = z.infer<typeof subscriptionSchema>;

export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const changePlanSchema = z.object({
  planKey: z.string().min(1).max(60),
  interval: z.enum(BILLING_INTERVALS).default('month'),
  licensedScreens: z.number().int().min(1).max(100000).optional(),
});

/** What a plan change produced: a checkout to open, a change already applied, or neither. */
export const checkoutResultSchema = z.object({
  /** A hosted payment page to send the customer to (redirect-based providers). */
  url: z.string().nullable(),
  /** A Paddle transaction to open in Paddle's checkout overlay. */
  paddleTransactionId: z.string().nullable(),
  subscription: subscriptionSchema.nullable(),
});
export type CheckoutResult = z.infer<typeof checkoutResultSchema>;

/** How the dashboard should take payments. The Paddle client token is public by design. */
export const billingConfigSchema = z.object({
  provider: z.string(),
  paddle: z
    .object({ environment: z.enum(['sandbox', 'production']), clientToken: z.string() })
    .nullable(),
});
export type BillingConfig = z.infer<typeof billingConfigSchema>;

/* ------------------------------------------------------------------ audit logs */

export const auditLogSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  actorType: z.string(),
  action: z.string(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  resourceName: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
});

export type AuditLog = z.infer<typeof auditLogSchema>;

export const auditQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  action: z.string().max(80).optional(),
  actorId: z.string().uuid().optional(),
  resourceType: z.string().max(60).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
});

/* -------------------------------------------------------------------- api keys */

export const apiKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
});
export type ApiKey = z.infer<typeof apiKeySchema>;

export const createApiKeySchema = z.object({
  name: nameSchema,
  expiresInDays: z.number().int().min(1).max(3650).optional(),
});

/* ----------------------------------------------------------------------- admin */

export const adminOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(ORGANIZATION_STATUSES),
  planKey: z.string(),
  subscriptionStatus: z.string(),
  userCount: z.number().int(),
  screenCount: z.number().int(),
  onlineScreens: z.number().int(),
  storageUsedBytes: z.number().int(),
  maxScreens: z.number().int(),
  maxStorageBytes: z.number().int(),
  createdAt: z.string(),
});

export type AdminOrganization = z.infer<typeof adminOrganizationSchema>;
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type AnalyticsOverview = z.infer<typeof analyticsOverviewSchema>;

export const adminUpdateOrganizationSchema = z.object({
  status: z.enum(ORGANIZATION_STATUSES).optional(),
  planKey: z.string().max(60).optional(),
  maxScreens: z.number().int().min(0).max(100000).optional(),
  maxStorageBytes: z.number().int().min(0).optional(),
  subscriptionStatus: z.enum(SUBSCRIPTION_STATUSES).optional(),
  trialEndsAt: z.string().nullable().optional(),
});

export const adminHealthSchema = z.object({
  organizations: z.object({
    total: z.number().int(),
    active: z.number().int(),
    suspended: z.number().int(),
  }),
  users: z.number().int(),
  screens: z.object({
    total: z.number().int(),
    online: z.number().int(),
    offline: z.number().int(),
  }),
  storageBytes: z.number().int(),
  media: z.object({
    total: z.number().int(),
    processing: z.number().int(),
    failed: z.number().int(),
  }),
  failedSyncs: z.number().int(),
  queue: z.object({
    waiting: z.number().int(),
    active: z.number().int(),
    failed: z.number().int(),
  }),
  database: z.object({ ok: z.boolean(), latencyMs: z.number() }),
  storageDriver: z.string(),
  version: z.string(),
  uptimeSec: z.number(),
});
